import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Form,
  OverlayTrigger,
  Tooltip,
  Modal,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import CreatableSelect from "react-select/creatable";
import { TagCategoryRule } from "src/docuclipper/DocuclipperTypes";
import { confirmAlert } from "react-confirm-alert";
import "./CategoryTable.scss";

type CategoryTableProps = {
  tags: Array<{
    id: string;
    tag: string;
    tagLabel?: string;
    regexes: string;
    jsonRules: TagCategoryRule;
  }>;
  systemKeywords?: Record<string, string[]>;
  onDeleteTag: (id: string) => void;
  onAddKeyword: (tagId: string, keyword: string, isPositive: boolean) => void;
  onRemoveKeyword: (
    tagId: string,
    keyword: string,
    isPositive: boolean
  ) => void;
  onSaveTag: (
    tagId: string,
    newTag: string,
    newTagLabel: string,
    regexes: string,
    jsonRules: any
  ) => void;
  onAddNewCategory?: (
    tag: string,
    tagLabel: string,
    keywords: string[],
    negativeKeywords: string[]
  ) => void;
  showAddNewRow?: boolean;
  onCancelAddNew?: () => void;
};

export const CategoryTable: React.FC<CategoryTableProps> = ({
  tags,
  systemKeywords = {},
  onDeleteTag,
  onAddKeyword,
  onRemoveKeyword,
  onSaveTag,
  onAddNewCategory,
  showAddNewRow = false,
  onCancelAddNew,
}) => {
  const [editingName, setEditingName] = useState<string | null>(null);
  const [editedTag, setEditedTag] = useState("");
  const [editedTagLabel, setEditedTagLabel] = useState("");

  // State for new category row
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newCategoryLabel, setNewCategoryLabel] = useState("");
  const [newPositiveKeywords, setNewPositiveKeywords] = useState<string[]>([]);
  const [newNegativeKeywords, setNewNegativeKeywords] = useState<string[]>([]);
  const [systemKeywordsModal, setSystemKeywordsModal] = useState<{
    tagName: string;
    keywords: string[];
  } | null>(null);

  const parseKeywords = (jsonRules, regexes) => {
    try {
      if (jsonRules) {
        const rules =
          typeof jsonRules === "string" ? JSON.parse(jsonRules) : jsonRules;
        const positiveKeywords =
          rules.conditions?.all?.find(
            (condition) => condition.operator === "containsAny"
          )?.value.keywords ||
          rules.keywords ||
          [];
        const negativeKeywords =
          rules.conditions?.all?.find(
            (condition) => condition.operator === "doesNotContainAny"
          )?.value.keywords ||
          rules.negativeKeywords ||
          [];

        return { positive: positiveKeywords, negative: negativeKeywords };
      }

      const parsedRegexes = regexes ? JSON.parse(regexes) : [];
      return { positive: parsedRegexes, negative: [] };
    } catch (error) {
      console.error("Error parsing jsonRules or regexes:", error);
      return { positive: [], negative: [] };
    }
  };

  const handleDeleteTag = (tagId: string, tagName: string) => {
    confirmAlert({
      customUI: ({ onClose }) => {
        return (
          <div
            style={{
              zIndex: 1500,
              background: "white",
              padding: "20px",
              borderRadius: "8px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            }}
          >
            <h3>Confirm Category Deletion</h3>
            <p>
              Are you sure you want to delete the category{" "}
              <strong>{tagName}</strong>?
            </p>

            <button className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button
              className="btn btn-danger ml-2"
              onClick={() => {
                onDeleteTag(tagId);
                onClose();
              }}
            >
              Delete
            </button>
          </div>
        );
      },
      overlayClassName: "overlay-custom-class-name",
    });
  };

  const startEditingName = (tag) => {
    setEditingName(tag.id);
    setEditedTag(tag.tag);
    setEditedTagLabel(tag.tagLabel || "");
  };

  const saveEditedName = (tag) => {
    // Parse existing keywords
    const keywords = parseKeywords(tag.jsonRules, tag.regexes);
    
    // Reconstruct the rule with the new tag name and existing keywords
    const updatedRule: TagCategoryRule = {
      keywords: keywords.positive,
      negativeKeywords: keywords.negative,
      tag: editedTag, // Use the NEW tag name
    };
    
    onSaveTag(tag.id, editedTag, editedTagLabel, tag.regexes, updatedRule);
    setEditingName(null);
  };

  const cancelEditingName = () => {
    setEditingName(null);
    setEditedTag("");
    setEditedTagLabel("");
  };

  const handleKeywordChange = (
    tag,
    newValue: any,
    actionMeta: any,
    isPositive: boolean
  ) => {
    if (tag.isAdmin) {
      return;
    }
    if (actionMeta.action === "create-option" && newValue) {
      onAddKeyword(tag.id, newValue[newValue.length - 1].value, isPositive);
    } else if (actionMeta.action === "remove-value") {
      onRemoveKeyword(tag.id, actionMeta.removedValue.value, isPositive);
    }
  };

  const handleSaveNewCategory = () => {
    if (newCategoryName.trim() && onAddNewCategory) {
      onAddNewCategory(
        newCategoryName.trim(),
        newCategoryLabel.trim(),
        newPositiveKeywords,
        newNegativeKeywords
      );
      // Reset form
      setNewCategoryName("");
      setNewCategoryLabel("");
      setNewPositiveKeywords([]);
      setNewNegativeKeywords([]);
    }
  };

  const handleCancelNewCategory = () => {
    setNewCategoryName("");
    setNewCategoryLabel("");
    setNewPositiveKeywords([]);
    setNewNegativeKeywords([]);
    if (onCancelAddNew) {
      onCancelAddNew();
    }
  };

  // Reset new category form when showAddNewRow changes
  useEffect(() => {
    if (showAddNewRow) {
      setNewCategoryName("");
      setNewCategoryLabel("");
      setNewPositiveKeywords([]);
      setNewNegativeKeywords([]);
    }
  }, [showAddNewRow]);

  return (
    <div className="category-table-container">
      <div className="table-wrapper">
        <Table className="mb-0">
          <thead>
            <tr>
              <th style={{ width: "20%" }}>Category Name</th>
              <th style={{ width: "30%" }}>
                Include Keywords{" "}
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip id="include-keywords-tooltip">
                      DocuClipper will assign this category if the transaction
                      description contains <strong>any</strong> of these
                      keywords
                    </Tooltip>
                  }
                >
                  <FontAwesomeIcon
                    icon="info-circle"
                    className="text-muted ml-1"
                    style={{ fontSize: "0.85rem" }}
                  />
                </OverlayTrigger>
              </th>
              <th style={{ width: "40%" }}>
                Exclude Keywords{" "}
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip id="exclude-keywords-tooltip">
                      DocuClipper will <strong>not</strong> assign this category
                      if the description contains any of these keywords, even if
                      include keywords match
                    </Tooltip>
                  }
                >
                  <FontAwesomeIcon
                    icon="info-circle"
                    className="text-muted ml-1"
                    style={{ fontSize: "0.85rem" }}
                  />
                </OverlayTrigger>
              </th>
              <th style={{ width: "10%" }} className="text-center">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {showAddNewRow && (
              <tr
                className="new-category-row"
                style={{ backgroundColor: "#fffacd" }}
              >
                <td>
                  <Form.Control
                    size="sm"
                    type="text"
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    placeholder="Category name *"
                    className="mb-1"
                    autoFocus
                  />
                  <Form.Control
                    size="sm"
                    type="text"
                    value={newCategoryLabel}
                    onChange={(e) => setNewCategoryLabel(e.target.value)}
                    placeholder="Label (optional)"
                  />
                </td>
                <td>
                  <CreatableSelect
                    placeholder="Type keyword and press Enter..."
                    isClearable
                    onChange={(newValue) => {
                      setNewPositiveKeywords(
                        (newValue || []).map((item) => item.value)
                      );
                    }}
                    isMulti={true}
                    value={newPositiveKeywords.map((k) => ({
                      label: k,
                      value: k,
                    }))}
                    formatCreateLabel={(val) => `Add "${val}"`}
                    styles={{
                      control: (base) => ({
                        ...base,
                        minHeight: "38px",
                        borderColor: "#28a745",
                      }),
                      multiValue: (base) => ({
                        ...base,
                        backgroundColor: "#d4edda",
                      }),
                      multiValueLabel: (base) => ({
                        ...base,
                        color: "#155724",
                      }),
                      multiValueRemove: (base) => ({
                        ...base,
                        color: "#155724",
                        ":hover": {
                          backgroundColor: "#c3e6cb",
                          color: "#155724",
                        },
                      }),
                    }}
                  />
                </td>
                <td>
                  <CreatableSelect
                    placeholder="Type keyword and press Enter..."
                    isClearable
                    onChange={(newValue) => {
                      setNewNegativeKeywords(
                        (newValue || []).map((item) => item.value)
                      );
                    }}
                    isMulti={true}
                    value={newNegativeKeywords.map((k) => ({
                      label: k,
                      value: k,
                    }))}
                    formatCreateLabel={(val) => `Add "${val}"`}
                    styles={{
                      control: (base) => ({
                        ...base,
                        minHeight: "38px",
                        borderColor: "#dc3545",
                      }),
                      multiValue: (base) => ({
                        ...base,
                        backgroundColor: "#f8d7da",
                      }),
                      multiValueLabel: (base) => ({
                        ...base,
                        color: "#721c24",
                      }),
                      multiValueRemove: (base) => ({
                        ...base,
                        color: "#721c24",
                        ":hover": {
                          backgroundColor: "#f5c6cb",
                          color: "#721c24",
                        },
                      }),
                    }}
                  />
                </td>
                <td className="text-center">
                  <Button
                    size="sm"
                    variant="success"
                    onClick={handleSaveNewCategory}
                    disabled={!newCategoryName.trim()}
                    className="mr-1"
                    title="Save new category"
                  >
                    <FontAwesomeIcon icon="check" />
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={handleCancelNewCategory}
                    title="Cancel"
                  >
                    <FontAwesomeIcon icon="times" />
                  </Button>
                </td>
              </tr>
            )}
            {tags.map((tag) => {
              const keywords = parseKeywords(tag.jsonRules, tag.regexes);
              const isEditingThisName = editingName === tag.id;
              const systemPositiveKeywords = systemKeywords[tag.tag] || [];
              const MAX_INLINE_SYSTEM_KEYWORDS = 4;
              const visibleSystemKeywords = systemPositiveKeywords.slice(
                0,
                MAX_INLINE_SYSTEM_KEYWORDS
              );
              const hiddenSystemKeywords = systemPositiveKeywords.slice(
                MAX_INLINE_SYSTEM_KEYWORDS
              );

              return (
                <tr key={tag.id}>
                  <td>
                    {isEditingThisName ? (
                      <div>
                        <Form.Control
                          size="sm"
                          type="text"
                          value={editedTag}
                          onChange={(e) => setEditedTag(e.target.value)}
                          placeholder="Category name"
                          className="mb-1"
                        />
                        <Form.Control
                          size="sm"
                          type="text"
                          value={editedTagLabel}
                          onChange={(e) => setEditedTagLabel(e.target.value)}
                          placeholder="Label (optional)"
                          className="mb-1"
                        />
                        <div>
                          <Button
                            size="sm"
                            variant="success"
                            onClick={() => saveEditedName(tag)}
                            className="mr-1"
                          >
                            <FontAwesomeIcon icon="check" />
                          </Button>
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={cancelEditingName}
                          >
                            <FontAwesomeIcon icon="times" />
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="d-flex align-items-center justify-content-between">
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div className="font-weight-bold text-truncate">
                            {tag.tag}
                          </div>
                          {tag.tagLabel && (
                            <div className="text-muted small text-truncate">
                              {tag.tagLabel}
                            </div>
                          )}
                        </div>
                        <Button
                          size="sm"
                          variant="link"
                          onClick={() => startEditingName(tag)}
                          className="p-0 ml-2 flex-shrink-0"
                          title="Edit category name"
                        >
                          <FontAwesomeIcon icon="pencil-alt" />
                        </Button>
                      </div>
                    )}
                  </td>
                  <td style={{ verticalAlign: "top", width: "30%" }}>
                    {systemPositiveKeywords.length > 0 && (
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip id={`system-keywords-${tag.id}`}>
                            System keywords are managed by DocuClipper and
                            can&apos;t be edited, but you can add your own
                            keywords below to customize this category.
                          </Tooltip>
                        }
                      >
                        <div
                          className="mb-2"
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "4px",
                          }}
                        >
                          {visibleSystemKeywords.map((keyword) => (
                            <span
                              key={`${tag.id}-system-${keyword}`}
                              className="badge badge-secondary d-inline-flex align-items-center"
                              style={{
                                fontSize: "0.75rem",
                                whiteSpace: "nowrap",
                                padding: "0.35rem 0.55rem",
                              }}
                            >
                              <FontAwesomeIcon
                                icon="lock"
                                className="mr-1"
                                style={{ fontSize: "0.7rem" }}
                              />
                              {keyword}
                            </span>
                          ))}
                          {hiddenSystemKeywords.length > 0 && (
                            <Button
                              variant="link"
                              size="sm"
                              className="p-0"
                              onClick={() =>
                                setSystemKeywordsModal({
                                  tagName: tag.tag,
                                  keywords: systemPositiveKeywords,
                                })
                              }
                            >
                              View all (+{hiddenSystemKeywords.length})
                            </Button>
                          )}
                        </div>
                      </OverlayTrigger>
                    )}
                    <CreatableSelect
                      data-cy={`creatable-select-positive-${tag.id}`}
                      placeholder="Type keyword and press Enter..."
                      isClearable
                      onChange={(newValue, actionMeta) =>
                        handleKeywordChange(tag, newValue, actionMeta, true)
                      }
                      isMulti={true}
                      options={keywords.positive.map((keyword) => ({
                        label: keyword,
                        value: keyword,
                      }))}
                      value={keywords.positive.map((keyword) => ({
                        label: keyword,
                        value: keyword,
                      }))}
                      formatCreateLabel={(val) => `Add "${val}"`}
                      styles={{
                        control: (base) => ({
                          ...base,
                          minHeight: "38px",
                          borderColor: "#28a745",
                        }),
                        multiValue: (base) => ({
                          ...base,
                          backgroundColor: "#d4edda",
                        }),
                        multiValueLabel: (base) => ({
                          ...base,
                          color: "#155724",
                        }),
                        multiValueRemove: (base) => ({
                          ...base,
                          color: "#155724",
                          ":hover": {
                            backgroundColor: "#c3e6cb",
                            color: "#155724",
                          },
                        }),
                      }}
                    />
                  </td>
                  <td style={{ verticalAlign: "top", width: "40%" }}>
                    <CreatableSelect
                      data-cy={`creatable-select-negative-${tag.id}`}
                      placeholder="Type keyword and press Enter..."
                      isClearable
                      onChange={(newValue, actionMeta) =>
                        handleKeywordChange(tag, newValue, actionMeta, false)
                      }
                      isMulti={true}
                      options={keywords.negative.map((keyword) => ({
                        label: keyword,
                        value: keyword,
                      }))}
                      value={keywords.negative.map((keyword) => ({
                        label: keyword,
                        value: keyword,
                      }))}
                      formatCreateLabel={(val) => `Add "${val}"`}
                      styles={{
                        control: (base) => ({
                          ...base,
                          minHeight: "38px",
                          borderColor: "#dc3545",
                        }),
                        multiValue: (base) => ({
                          ...base,
                          backgroundColor: "#f8d7da",
                        }),
                        multiValueLabel: (base) => ({
                          ...base,
                          color: "#721c24",
                        }),
                        multiValueRemove: (base) => ({
                          ...base,
                          color: "#721c24",
                          ":hover": {
                            backgroundColor: "#f5c6cb",
                            color: "#721c24",
                          },
                        }),
                      }}
                    />
                  </td>
                  <td className="text-center">
                    <Button
                      size="sm"
                      variant="outline-danger"
                      onClick={() => handleDeleteTag(tag.id, tag.tag)}
                      title="Delete category"
                    >
                      <FontAwesomeIcon icon="trash" />
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
      {tags.length === 0 && (
        <div className="text-center text-muted py-4">
          <p>
            No categories yet. Use "Add New" to create your first category and start organizing transactions automatically.
          </p>
        </div>
      )}
      <Modal
        show={!!systemKeywordsModal}
        onHide={() => setSystemKeywordsModal(null)}
        centered
      >
        {/* @ts-ignore - react-bootstrap 1.6.1 type definition issue */}
        <Modal.Header closeButton>
          <Modal.Title>System Keywords</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {systemKeywordsModal && (
            <>
              <p className="text-muted">
                These keywords are provided by DocuClipper for{" "}
                <strong>{systemKeywordsModal.tagName}</strong> and cannot be
                edited or removed.
              </p>
              <div
                style={{
                  maxHeight: "300px",
                  overflowY: "auto",
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "6px",
                }}
              >
                {systemKeywordsModal.keywords.map((keyword) => (
                  <span
                    key={`${systemKeywordsModal.tagName}-modal-${keyword}`}
                    className="badge badge-secondary d-inline-flex align-items-center"
                    style={{
                      fontSize: "0.85rem",
                      whiteSpace: "nowrap",
                      padding: "0.4rem 0.6rem",
                    }}
                  >
                    <FontAwesomeIcon
                      icon="lock"
                      className="mr-1"
                      style={{ fontSize: "0.75rem" }}
                    />
                    {keyword}
                  </span>
                ))}
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setSystemKeywordsModal(null)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CategoryTable;
